import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getVendorsBySearch, getVendorReport } from '../../api/ReportsAPI/viewVendorAPI';
import { getVendorDetailsByCode } from '../../api/PurchaseAPI/vendorCreationAPI';

// 'SelectAll' (no space) is the literal spGetVendorReport compares @Vendor against.
export const SELECT_ALL = 'SelectAll';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || (typeof e === 'string' ? e : fallback);

export const fetchVVVendorOptions = createAsyncThunk('viewVendor/fetchVendorOptions', async ({ vendorType, subType }, { rejectWithValue }) => {
    try { const res = await getVendorsBySearch(vendorType, subType); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch vendors')); }
});

export const fetchVVReport = createAsyncThunk('viewVendor/fetchReport', async (params, { rejectWithValue }) => {
    try { const res = await getVendorReport(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch vendors')); }
});

// Full vendor record for an expanded row, cached per vendor code.
export const fetchVVVendorDetail = createAsyncThunk('viewVendor/fetchVendorDetail', async ({ vendorCode, roleId }, { rejectWithValue }) => {
    try { const res = await getVendorDetailsByCode(vendorCode, roleId); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch vendor details')); }
});

const initialState = {
    vendorOptions: [], vendorOptionsLoading: false,
    report: [], reportLoading: false, reportError: null,
    details: {}, detailsLoading: {}, detailsError: {},
};

const viewVendorSlice = createSlice({
    name: 'viewVendor',
    initialState,
    reducers: {
        clearVVVendorOptions: (s) => { s.vendorOptions = []; },
        clearVVReport: (s) => { s.report = []; s.reportError = null; },
        resetViewVendor: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVVVendorOptions.pending, (s) => { s.vendorOptionsLoading = true; })
            .addCase(fetchVVVendorOptions.fulfilled, (s, a) => { s.vendorOptionsLoading = false; s.vendorOptions = a.payload; })
            .addCase(fetchVVVendorOptions.rejected, (s) => { s.vendorOptionsLoading = false; s.vendorOptions = []; });

        builder
            .addCase(fetchVVReport.pending, (s) => { s.reportLoading = true; s.reportError = null; })
            .addCase(fetchVVReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
            .addCase(fetchVVReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; s.report = []; });

        builder
            .addCase(fetchVVVendorDetail.pending, (s, a) => {
                const k = a.meta.arg.vendorCode;
                s.detailsLoading[k] = true; delete s.detailsError[k];
            })
            .addCase(fetchVVVendorDetail.fulfilled, (s, a) => {
                const k = a.meta.arg.vendorCode;
                s.detailsLoading[k] = false; s.details[k] = a.payload;
            })
            .addCase(fetchVVVendorDetail.rejected, (s, a) => {
                const k = a.meta.arg.vendorCode;
                s.detailsLoading[k] = false; s.detailsError[k] = a.payload;
            });
    },
});

export const { clearVVVendorOptions, clearVVReport, resetViewVendor } = viewVendorSlice.actions;

export const selectVVVendorOptions        = (s) => s.viewVendor.vendorOptions;
export const selectVVVendorOptionsLoading = (s) => s.viewVendor.vendorOptionsLoading;
export const selectVVReport               = (s) => s.viewVendor.report;
export const selectVVReportLoading        = (s) => s.viewVendor.reportLoading;
export const selectVVReportError          = (s) => s.viewVendor.reportError;
export const selectVVDetails              = (s) => s.viewVendor.details;
export const selectVVDetailsLoading       = (s) => s.viewVendor.detailsLoading;
export const selectVVDetailsError         = (s) => s.viewVendor.detailsError;

export default viewVendorSlice.reducer;
